import type { ProgramSlideLayoutSubsetV1 } from '../types/evaluationPlanSheetCanvas';
import { createEmptySheetLayoutSubset } from '../types/evaluationPlanSheetCanvas';
import {
  normalizeProgramWorksheetRegistryName,
  PROGRAM_WORKSHEET_REGISTERED_PAGE_MAX_COUNT,
  programWorksheetRegistryFallbackName,
} from './programWorksheetRegistryLimits';

const STORAGE_PREFIX = 'cp.programWorksheetRegisteredPages.v1:';
export const PROGRAM_WORKSHEET_REGISTERED_PAGES_CHANGED_EVENT =
  'cp:program-worksheet-registered-pages-changed';

export interface ProgramWorksheetRegisteredPage {
  id: string;
  name: string;
  layout: ProgramSlideLayoutSubsetV1;
  order: number;
  updatedAt: number;
}

function storageKey(workspaceId: string): string {
  return `${STORAGE_PREFIX}${workspaceId.trim()}`;
}

function safeStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function normalizeLayout(raw: unknown): ProgramSlideLayoutSubsetV1 {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return createEmptySheetLayoutSubset();
  }
  return raw as ProgramSlideLayoutSubsetV1;
}

function normalizePages(raw: unknown): ProgramWorksheetRegisteredPage[] {
  if (!Array.isArray(raw)) return [];
  const out: ProgramWorksheetRegisteredPage[] = [];
  raw.forEach((item, idx) => {
    if (!item || typeof item !== 'object') return;
    const o = item as {
      id?: unknown;
      name?: unknown;
      layout?: unknown;
      order?: unknown;
      updatedAt?: unknown;
    };
    const id = String(o.id ?? '').trim();
    if (!id) return;
    out.push({
      id,
      name: normalizeProgramWorksheetRegistryName(o.name, programWorksheetRegistryFallbackName(idx)),
      layout: normalizeLayout(o.layout),
      order: Number.isFinite(Number(o.order)) && Number(o.order) >= 0 ? Number(o.order) : idx,
      updatedAt:
        Number.isFinite(Number(o.updatedAt)) && Number(o.updatedAt) > 0
          ? Number(o.updatedAt)
          : Date.now(),
    });
  });
  return out.sort((a, b) => a.order - b.order);
}

function emitChanged(workspaceId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent(PROGRAM_WORKSHEET_REGISTERED_PAGES_CHANGED_EVENT, {
        detail: { workspaceId: workspaceId.trim() },
      })
    );
  } catch {
    /* ignore */
  }
}

export function loadProgramWorksheetRegisteredPages(
  workspaceId: string
): ProgramWorksheetRegisteredPage[] {
  const sid = workspaceId.trim();
  if (!sid) return [];
  const st = safeStorage();
  if (!st) return [];
  try {
    const raw = st.getItem(storageKey(sid));
    if (!raw) return [];
    return normalizePages(JSON.parse(raw)).slice(0, PROGRAM_WORKSHEET_REGISTERED_PAGE_MAX_COUNT);
  } catch {
    return [];
  }
}

export function saveProgramWorksheetRegisteredPages(
  workspaceId: string,
  pages: ProgramWorksheetRegisteredPage[]
): void {
  const sid = workspaceId.trim();
  if (!sid) return;
  const st = safeStorage();
  if (!st) return;
  try {
    const payload = pages
      .map((p, idx) => ({
        ...p,
        order: idx,
        name: normalizeProgramWorksheetRegistryName(p.name, programWorksheetRegistryFallbackName(idx)),
        updatedAt: Number.isFinite(p.updatedAt) ? p.updatedAt : Date.now(),
      }))
      .filter((p) => p.id.trim())
      .slice(0, PROGRAM_WORKSHEET_REGISTERED_PAGE_MAX_COUNT);
    st.setItem(storageKey(sid), JSON.stringify(payload));
    emitChanged(sid);
  } catch {
    /* ignore */
  }
}
